<template>
  <div class="popup_in">
    <div class="popup" dir="rtl">
      <div class="popup_close popup_close-25 close-animate">
        <a href="javascript:void(0);" @click="$emit('close')"><CloseBlue /></a>
      </div>

      <div v-if="isComplete" class="popup-content">
        <img class="status-icon" src="/assets/img/svg/ok-green.svg" alt="" />
        <div class="title--32 tx--blue2">בקשות המטופל נשלחו בהצלחה</div>
        <div class="pop-delete-block-btns flex">
          <a
            class="bt_blue bt-approveDelete m-100"
            href="javascript:void(0);"
            @click.prevent="$emit('close')"
          >
            סגור
          </a>
        </div>
      </div>

      <div v-else-if="isLoading" class="popup-content">
        <div class="spinner"></div>
        <div class="title--32 tx--blue2">טוען בקשות מטופל...</div>
      </div>

      <div v-else-if="loadError" class="popup-content">
        <div class="title--32 tx--blue2">
          בקשות מטופל לבית החולים
        </div>
        <div class="patient-requests-error">{{ loadError }}</div>

        <div class="pop-delete-block-btns flex">
          <a
            class="bt_blue bt-approveDelete m-100"
            href="javascript:void(0);"
            @click.prevent="$emit('close')"
          >
            סגור
          </a>
        </div>
      </div>

      <div v-else class="popup-content">
        <div class="title--32 tx--blue2">
          בקשות מטופל לבית החולים
        </div>
        <PatientRequestsForm v-model="formData" />

        <div v-if="errorMessage" class="patient-requests-error">
          {{ errorMessage }}
        </div>

        <div class="pop-delete-block-btns flex">
          <a
            class="bt_blue bt_border m-100"
            href="javascript:void(0);"
            @click.prevent="$emit('close')"
          >
            ביטול
          </a>
          <a
            class="bt_blue bt-approveDelete m-100"
            :class="{
              disabled: !canSubmit || isSubmitting
            }"
            href="javascript:void(0);"
            @click.prevent="submit"
          >
            {{ isSubmitting ? "שולח..." : "שליחה" }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CloseBlue from "@/assets/img/svg/close-blue.svg";
import PatientRequestsForm from "@/components/Dashboard/Surgery/PatientRequestsForm.vue";
import {
  buildPatientRequestPayloads,
  createEmptyPatientRequests,
  getPatientRequestTypes,
  isPatientRequestNotesRequired,
  parsePatientRequests,
  PATIENT_REQUEST_CATEGORIES_ENABLED
} from "@/utilities/patient-requests";

export default {
  name: "PatientRequestsPopup",
  components: {
    CloseBlue,
    PatientRequestsForm
  },
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      formData: createEmptyPatientRequests(),
      // Types already saved on the surgery, so they are not sent twice
      sentTypes: [],
      isLoading: true,
      isSubmitting: false,
      isComplete: false,
      loadError: "",
      errorMessage: ""
    };
  },
  computed: {
    newTypes() {
      return this.formData.types.filter(type => !this.sentTypes.includes(type));
    },
    notesRequired() {
      return isPatientRequestNotesRequired(this.newTypes);
    },
    canSubmit() {
      if (PATIENT_REQUEST_CATEGORIES_ENABLED) {
        if (!this.newTypes.length) return false;
        if (this.notesRequired && !this.formData.notes.trim()) return false;
        return true;
      }

      return !!this.formData.notes.trim();
    }
  },
  async mounted() {
    try {
      const [info, orders] = await Promise.all([
        this.$API.getSurgeryInfo(this.event.id),
        this.$API.getSurgeryOrders(this.event.id)
      ]);

      if (info?.status !== "OK") {
        throw new Error("Surgery data is unavailable");
      }

      this.formData = parsePatientRequests(info, orders);
      this.sentTypes = [...this.formData.types];
    } catch (error) {
      this.loadError = "לא ניתן היה לטעון את בקשות המטופל. נסו שוב.";
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async submit() {
      if (!this.canSubmit || this.isSubmitting) {
        return;
      }

      if (this.notesRequired && !this.formData.notes.trim()) {
        this.errorMessage = "פירוט הבקשה הוא שדה חובה";
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = "";

      const payloads = buildPatientRequestPayloads(
        this.event.id,
        this.formData,
        this.sentTypes
      );
      const results = await Promise.all(
        payloads.map(async payload => {
          try {
            const response = await this.$API.savePatientRequest(payload);
            return {
              category: payload.category,
              success: response?.status !== "ERROR"
            };
          } catch (error) {
            return { category: payload.category, success: false };
          }
        })
      );

      if (!PATIENT_REQUEST_CATEGORIES_ENABLED) {
        if (results.length && results.every(result => result.success)) {
          this.isComplete = true;
        } else {
          this.errorMessage =
            "לא ניתן היה לשלוח את הבקשות. נסו שוב מאוחר יותר.";
        }
        this.isSubmitting = false;
        return;
      }

      // Whatever went through is now saved, so a retry only sends the rest
      const succeededTypes = getPatientRequestTypes(
        results.filter(result => result.success).map(result => result.category)
      );
      this.sentTypes = [...this.sentTypes, ...succeededTypes];

      if (succeededTypes.length === results.length) {
        this.isComplete = true;
      } else {
        this.errorMessage = succeededTypes.length
          ? "חלק מהבקשות נשלחו. ניתן לנסות שוב לשלוח את הבקשות שנותרו."
          : "לא ניתן היה לשלוח את הבקשות. נסו שוב מאוחר יותר.";
      }

      this.isSubmitting = false;
    }
  }
};
</script>

<style scoped>
.popup-content {
  text-align: right;
}

.title--32 {
  margin-bottom: 24px;
  text-align: center;
}

.patient-requests-error {
  color: #b00020;
  margin-top: 22px;
  text-align: center;
}

.status-icon {
  display: block;
  margin: 0 auto 15px;
  width: 42px;
}

.spinner {
  animation: spin 0.8s linear infinite;
  border: 4px solid #e0e0e0;
  border-radius: 50%;
  border-top-color: #324997;
  height: 42px;
  margin: 0 auto 15px;
  width: 42px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
